import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import API from '../utils/axios';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 401s from any request clear the session via a custom event (SPA nav, no reload loop).
    const onAuthLogout = () => setUser(null);
    window.addEventListener('auth:logout', onAuthLogout);
    return () => window.removeEventListener('auth:logout', onAuthLogout);
  }, []);

  useEffect(() => {
    // Restore a stored session ONLY after the backend confirms it's still valid.
    // Stale sessions (backend in-memory DB reseeds on restart -> old user _id) must
    // be silently cleared instead of causing repeated 401 redirects.
    const stored = localStorage.getItem('user');
    if (!stored) { setLoading(false); return; }
    const parsed = JSON.parse(stored);
    API.get('/auth/profile').then(({ data }) => {
      const { password, ...profile } = data;
      const merged = { ...parsed, ...profile, permissions: profile.permissions || {} };
      localStorage.setItem('user', JSON.stringify(merged));
      setUser(merged);
    }).catch(() => {
      localStorage.removeItem('user');
    }).finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await API.post('/auth/login', { email, password });
    localStorage.setItem('user', JSON.stringify(data));
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  const updateUser = useCallback((updates) => {
    const updated = { ...user, ...updates };
    localStorage.setItem('user', JSON.stringify(updated));
    setUser(updated);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, updateUser, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
